import React from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import { FaReact, FaNodeJs, FaCode, FaCube, FaPalette } from "react-icons/fa";
import { FaGlobe, FaUserAstronaut } from "react-icons/fa6";

const AboutSection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled.div`
  h2 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }
`;

const SkillsSection = styled.div`
  h3 {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SkillItem = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 1rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.1rem;

  span {
    font-size: 1.5rem;
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  border-radius: 15px;

  .number {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .label {
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const skills = [
  { name: "React & Vue.js", icon: <FaReact /> },
  { name: "Node.js", icon: <FaNodeJs /> },
  { name: "JavaScript & TypeScript", icon: <FaCode /> },
  { name: "Webgl & Three.js", icon: <FaCube /> },
  { name: "UI/UX 设计", icon: <FaPalette /> },
  { name: "Web3D 开发", icon: <FaGlobe /> },
];

const stats = [
  { number: "30+", label: "完成项目" },
  { number: "7+", label: "年经验" },
  { number: "25+", label: "满意客户" },
];

const About = () => {
  return (
    <AboutSection>
      <ContentWrapper>
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <AboutContent>
            <h2>关于我</h2>
            <p>
              我是一名充满热情的全栈开发者，拥有超过7年的经验，
              致力于创造有意义的数字解决方案。我的技术之旅始于好奇心，
              并发展成为专注于构建可扩展、用户友好的应用程序的职业生涯。
            </p>
            <p>
              我专精于现代网络技术，在前端和web3D开发方面都有扎实的基础。
              当我不在编程时，你可以在探索新技术、参与开源项目或指导有抱负的开发者中找到我。
            </p>

            <SkillsSection>
              <h3>技术技能</h3>
              <SkillsGrid>
                {skills.map((skill, index) => (
                  <SkillItem
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <span>{skill.icon}</span>
                    <span>{skill.name}</span>
                  </SkillItem>
                ))}
              </SkillsGrid>
            </SkillsSection>
          </AboutContent>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <div
              style={{
                width: "350px",
                height: "350px",
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "2rem",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "4rem", marginBottom: "1rem" }}><FaUserAstronaut /></div>
              <h3 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
                联系我们
              </h3>
              <p style={{ opacity: 0.9 }}>准备好让您的想法变为现实了吗？</p>
            </div>
          </div>

          <StatsSection>
            {stats.map((stat, index) => (
              <StatItem
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
              >
                <div className="number">{stat.number}</div>
                <div className="label">{stat.label}</div>
              </StatItem>
            ))}
          </StatsSection>
        </motion.div>
      </ContentWrapper>
    </AboutSection>
  );
};

export default About;
